/**
 * Auth/role helpers for route guards (N3).
 *
 * The SPA's "am I signed in" signal is the cached user in localStorage
 * ('bc-portal-user'), the same thing App.tsx restores on load. With cookie
 * sessions (#103) the token isn't readable from JS, so the server stays the
 * real authority: a stale cache is cleared by the existing 401 → auth:failed
 * flow, which sends the user to /login?redirect=… .
 */
import type { CurrentUser } from '../types';

export type Role = 'global_admin' | 'reseller_admin' | 'tenant_admin' | 'user' | string;

export interface AuthState {
  /** Current signed-in user, or null. Read fresh on every guard run. */
  getUser: () => CurrentUser | null;
}

/** Default auth source: the cached user, ignored while a cross-subdomain auth transfer is in progress. */
export const storageAuth: AuthState = {
  getUser: () => {
    try {
      if (typeof document !== 'undefined' && document.cookie.includes('portal_auth_transfer=')) {return null;}
      const raw = localStorage.getItem('bc-portal-user');
      return raw ? (JSON.parse(raw) as CurrentUser) : null;
    } catch {
      return null;
    }
  },
};

export type AdminArea = 'glbadm' | 'rsladm' | 'tntadm';

/** Same rules App.tsx/AppRouter.tsx applied inline before the router. */
export function canAccessArea(user: CurrentUser | null, area: AdminArea | 'admin'): boolean {
  if (!user) {return false;}
  const role = user.role;
  switch (area) {
    // AppRouter only renders the global shell for role === 'global_admin'; the old
    // App.tsx check also let an impersonating global admin through, straight into
    // "Access denied". They now go to their (impersonated) home instead.
    case 'glbadm': return role === 'global_admin';
    case 'rsladm': return role === 'reseller_admin';
    case 'tntadm': return role === 'global_admin' || role === 'tenant_admin' || role === 'reseller_admin';
    case 'admin': return role === 'global_admin' || role === 'tenant_admin';
  }
}

/** Where a signed-in user lands by default. */
export function homePathFor(user: CurrentUser | null): '/' | '/glbadm' | '/rsladm' | '/tntadm' {
  switch (user?.role) {
    case 'global_admin': return '/glbadm';
    case 'reseller_admin': return '/rsladm';
    case 'tenant_admin': return '/tntadm';
    default: return '/';
  }
}

/**
 * Only same-origin, app-relative return paths are accepted (no open redirect):
 * must start with a single "/", no "//", no backslashes, no scheme, and never
 * back to the sign-in page itself.
 */
export function safeRedirect(target: unknown): string | null {
  if (typeof target !== 'string' || target.length === 0 || target.length > 2048) {return null;}
  if (!target.startsWith('/') || target.startsWith('//') || target.includes('\\')) {return null;}
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f]/.test(target)) {return null;}
  try {
    const u = new URL(target, 'https://portal.invalid');
    if (u.origin !== 'https://portal.invalid') {return null;}
    if (u.pathname === '/login' || /^\/[^/]+\/login$/.test(u.pathname)) {return null;}
    return u.pathname + u.search + u.hash;
  } catch {
    return null;
  }
}
